/* =====================================================================
 * useTableControls — 테이블 드래그 & 드롭 + 행/열 추가 버튼
 * ===================================================================== */

import { useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/react';
import type { Node } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';

/* ------------------------------------------------------------------ */
/* 훅 본체                                                              */
/* ------------------------------------------------------------------ */

interface UseTableControlsOptions {
  editor: Editor | null;
}

export function useTableControls({ editor }: UseTableControlsOptions) {
  /* 테이블 드래그 추적 — drop 핸들러에서 사용 */
  const tableDragRef = useRef<{ node: Node; pos: number } | null>(null);
  const TABLE_DEBUG = import.meta.env.DEV;

  const getReadyEditorView = () => {
    if (!editor || editor.isDestroyed || !editor.isInitialized) return null;
    try {
      const view = editor.view;
      if (!view || view.isDestroyed) return null;
      return view;
    } catch {
      return null;
    }
  };

  /* ── 테이블 드래그 & 드롭 (캡처 페이즈 — ProseMirror보다 먼저 실행) ── */
  useEffect(() => {
    if (!editor) return;
    let rafId: number | null = null;
    let cleanup: (() => void) | null = null;

    const attach = () => {
      const view = getReadyEditorView();
      if (!view) {
        rafId = requestAnimationFrame(attach);
        return;
      }

      const dom = view.dom;
      const onDrop = (e: DragEvent) => {
        const drag = tableDragRef.current;
        if (!drag) return; // 비-테이블 → ProseMirror 기본 처리

        const activeView = getReadyEditorView();
        if (!activeView) {
          tableDragRef.current = null;
          return;
        }

        // 우리가 처리 — ProseMirror 핸들러 차단
        e.preventDefault();
        e.stopImmediatePropagation();
        tableDragRef.current = null;
        (activeView as any).dragging = null;

        const posResult = activeView.posAtCoords({
          left: e.clientX,
          top: e.clientY,
        });
        if (!posResult) return;

        try {
          const { state, dispatch } = activeView;
          const tr = state.tr;

          // 1) 원본 테이블 삭제
          tr.setSelection(NodeSelection.create(tr.doc, drag.pos));
          tr.deleteSelection();

          // 2) 삭제 후 문서에서 drop 위치 재계산
          const mappedMouse = tr.mapping.map(posResult.pos);
          const clamped = Math.min(Math.max(0, mappedMouse), tr.doc.content.size);
          const $pos = tr.doc.resolve(clamped);

          // 3) 가장 가까운 최상위 블록 경계 찾기
          let insertAt: number;
          if ($pos.depth === 0) {
            insertAt = clamped;
          } else {
            const blockStart = $pos.start(1);
            const blockNode = $pos.node(1);
            const relOffset = clamped - blockStart;
            insertAt = relOffset < blockNode.content.size / 2
              ? $pos.before(1)
              : $pos.after(1);
          }

          // 4) 테이블 삽입
          tr.insert(insertAt, drag.node);

          // 5) 삽입된 테이블 선택
          const $ins = tr.doc.resolve(insertAt);
          if ($ins.nodeAfter?.type.name === 'table') {
            tr.setSelection(NodeSelection.create(tr.doc, insertAt));
          }

          dispatch(tr.scrollIntoView().setMeta('uiEvent', 'drop'));
          activeView.focus();
        } catch { /* 위치 오류 시 무시 */ }

        // dropcursor 실선 강제 제거
        requestAnimationFrame(() => {
          dom.dispatchEvent(new DragEvent('dragleave', { bubbles: true }));
          dom.dispatchEvent(new DragEvent('dragend', { bubbles: true }));
        });
      };

      dom.addEventListener('drop', onDrop, true); // capture phase
      cleanup = () => dom.removeEventListener('drop', onDrop, true);
    };

    attach();
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      cleanup?.();
    };
  }, [editor]);

  /* ── 테이블 행/열 추가 버튼 (노션 스타일) ── */
  useEffect(() => {
    if (!editor) return;
    const ATTR = 'data-table-controls';
    const READY_ATTR = 'data-table-controls-ready';
    const ACTIVE_ATTR = 'data-table-controls-active';
    const COL_GAP = 6; // 테이블 우측 여백 내에서 보이도록 축소
    const ROW_GAP = 8; // 테이블 ↔ 행추가 버튼 간격 (더 타이트)
    const ROW_SCROLL_EXTRA_GAP = 10; // 스크롤바가 있을 때 행추가 버튼 추가 여백
    let lastMouseDownInTableAt = 0;
    let lastMouseDownTag = '';
    let lastMouseDownKind = '';
    const disposers: Array<() => void> = [];

    const debugLog = (label: string, payload?: Record<string, unknown>) => {
      if (!TABLE_DEBUG) return;
      const w = window as Window & { __TABLE_DEBUG_LOGS__?: Array<Record<string, unknown>> };
      const entry: Record<string, unknown> = {
        at: new Date().toISOString(),
        label,
        ...(payload ? { payload } : {}),
      };
      if (!w.__TABLE_DEBUG_LOGS__) w.__TABLE_DEBUG_LOGS__ = [];
      w.__TABLE_DEBUG_LOGS__.push(entry);
      if (w.__TABLE_DEBUG_LOGS__.length > 500) {
        w.__TABLE_DEBUG_LOGS__.splice(0, w.__TABLE_DEBUG_LOGS__.length - 500);
      }

      if (payload) {
        // eslint-disable-next-line no-console
        console.log(`[TableDebug] ${label}`, payload);
      } else {
        // eslint-disable-next-line no-console
        console.log(`[TableDebug] ${label}`);
      }
    };

    const w = window as Window & {
      __TABLE_DEBUG_LOGS__?: Array<Record<string, unknown>>;
      getTableDebugSummary?: () => Record<string, unknown>;
    };
    if (TABLE_DEBUG) {
      w.getTableDebugSummary = () => {
        const logs = (w.__TABLE_DEBUG_LOGS__ || []) as Array<{
          at?: string;
          label?: string;
          payload?: Record<string, unknown>;
        }>;
        const byLabel = (label: string) => logs.filter((l) => l.label === label);
        const colBtnTriggered = byLabel('col-btn-triggered');
        const rowBtnTriggered = byLabel('row-btn-triggered');
        const mismatch = byLabel('colgroup-count-mismatch');
        const zeroWidth = byLabel('fixed-zero-width-cols');
        const docChanged = byLabel('doc-changed-near-table-mousedown');
        const mouseDown = byLabel('mousedown-in-table');

        const toMs = (s?: string) => (s ? new Date(s).getTime() : 0);
        const suspiciousColBtnTrigger = colBtnTriggered.some((trigger) => {
          const t = toMs(trigger.at);
          const nearestMouse = [...mouseDown]
            .reverse()
            .find((m) => Math.abs(toMs(m.at) - t) < 400);
          if (!nearestMouse) return false;
          const kind = nearestMouse.payload?.targetKind;
          return kind !== 'col-btn';
        });

        let probableCause = 'insufficient-data';
        if (suspiciousColBtnTrigger) {
          probableCause = 'hidden-col-button-intercept';
        } else if (mismatch.length > 0 || zeroWidth.length > 0) {
          probableCause = 'colgroup-desync';
        } else if (docChanged.length > 0 && colBtnTriggered.length === 0 && rowBtnTriggered.length === 0) {
          probableCause = 'unexpected-doc-change-after-mousedown';
        }

        return {
          totalLogs: logs.length,
          counts: {
            mouseDown: mouseDown.length,
            colBtnTriggered: colBtnTriggered.length,
            rowBtnTriggered: rowBtnTriggered.length,
            colgroupMismatch: mismatch.length,
            fixedZeroWidthCols: zeroWidth.length,
            docChangedNearMouseDown: docChanged.length,
          },
          suspiciousColBtnTrigger,
          probableCause,
          lastLogs: logs.slice(-20),
        };
      };
    }

    const getDomTableShape = (table: HTMLTableElement) => {
      const rows = Array.from(table.querySelectorAll('tr'));
      const firstRow = rows[0];
      const firstRowCells = firstRow ? Array.from(firstRow.children) as HTMLElement[] : [];
      const firstRowColspanTotal = firstRowCells.reduce((sum, cell) => {
        const colspan = Number(cell.getAttribute('colspan') || 1);
        return sum + (Number.isFinite(colspan) && colspan > 0 ? colspan : 1);
      }, 0);
      const colgroupCols = table.querySelectorAll('colgroup > col').length;
      const colWidths = Array.from(table.querySelectorAll('colgroup > col')).map((col, idx) => ({
        idx,
        widthAttr: col.getAttribute('width'),
        widthStyle: (col as HTMLElement).style.width,
        minWidthStyle: (col as HTMLElement).style.minWidth,
      }));
      return {
        rowCount: rows.length,
        firstRowCellCount: firstRowCells.length,
        firstRowColspanTotal,
        colgroupCols,
        colWidths,
      };
    };

    const getDocTableShapeAtSelection = () => {
      const { selection } = editor.state;
      const $from = selection.$from;
      for (let d = $from.depth; d >= 1; d -= 1) {
        const node = $from.node(d);
        if (node.type.name !== 'table') continue;
        const rowCells = Array.from({ length: node.childCount }).map((_, rowIdx) => {
          const row = node.child(rowIdx);
          const colspans = Array.from({ length: row.childCount }).map((__, cellIdx) => {
            const cell = row.child(cellIdx);
            return Number(cell.attrs.colspan || 1);
          });
          return {
            rowIdx,
            cellCount: row.childCount,
            colspanTotal: colspans.reduce((sum, n) => sum + (Number.isFinite(n) && n > 0 ? n : 1), 0),
            colspans,
          };
        });
        return { depth: d, rowCount: node.childCount, rowCells };
      }
      return null;
    };

    const getFirstRowColCount = (table: HTMLTableElement) => {
      const firstRow = table.querySelector('tr');
      if (!firstRow) return 0;
      const cells = Array.from(firstRow.children) as HTMLElement[];
      return cells.reduce((sum, cell) => {
        const colspan = Number(cell.getAttribute('colspan') || 1);
        return sum + (Number.isFinite(colspan) && colspan > 0 ? colspan : 1);
      }, 0);
    };

    const getColGroup = (table: HTMLTableElement) => {
      const children = Array.from(table.children) as HTMLElement[];
      let colgroup = children.find((el) => el.tagName === 'COLGROUP') as HTMLTableColElement | undefined;
      if (!colgroup) {
        colgroup = document.createElement('colgroup');
        table.insertBefore(colgroup, table.firstChild);
      }
      return colgroup;
    };

    const normalizeColGroup = (table: HTMLTableElement) => {
      const colCount = getFirstRowColCount(table);
      if (colCount <= 0) return;
      const colgroup = getColGroup(table);
      const cols = Array.from(colgroup.querySelectorAll('col'));

      const rebuild = () => {
        while (colgroup.firstChild) colgroup.removeChild(colgroup.firstChild);
        for (let i = 0; i < colCount; i += 1) {
          const col = document.createElement('col');
          col.style.minWidth = '80px';
          colgroup.appendChild(col);
        }
      };

      if (cols.length !== colCount) {
        debugLog('colgroup-count-mismatch', {
          expectedCols: colCount,
          actualCols: cols.length,
          domShape: getDomTableShape(table),
          docShapeAtSelection: getDocTableShapeAtSelection(),
        });
        rebuild();
        debugLog('colgroup-rebuilt', { domShape: getDomTableShape(table) });
        return;
      }

      let fixedZeroWidthCols = 0;
      cols.forEach((col) => {
        const attrWidth = col.getAttribute('width');
        const styleWidth = col.style.width;
        const parsedAttr = attrWidth ? Number(attrWidth.replace('px', '')) : NaN;
        const parsedStyle = styleWidth ? Number(styleWidth.replace('px', '')) : NaN;

        // 0폭/음수폭 열이 있으면 시각적 "숨은 열"이 생겨 클릭 좌표가 한 칸씩 밀린다.
        if ((Number.isFinite(parsedAttr) && parsedAttr <= 0) || (Number.isFinite(parsedStyle) && parsedStyle <= 0)) {
          col.removeAttribute('width');
          col.style.width = '';
          col.style.minWidth = '80px';
          fixedZeroWidthCols += 1;
        }
      });

      if (fixedZeroWidthCols > 0) {
        debugLog('fixed-zero-width-cols', {
          fixedZeroWidthCols,
          domShape: getDomTableShape(table),
          docShapeAtSelection: getDocTableShapeAtSelection(),
        });
      }
    };

    const isRowBtnAligned = (table: HTMLElement, btn: HTMLElement) => {
      const tableRect = table.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      return (
        btnRect.top >= tableRect.bottom - 2 &&
        btnRect.left <= tableRect.left + 2 &&
        btnRect.right >= tableRect.right - 2 &&
        btnRect.height <= 28
      );
    };

    const isColBtnAligned = (table: HTMLElement, btn: HTMLElement) => {
      const tableRect = table.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      return (
        btnRect.left >= tableRect.right - 2 &&
        btnRect.top <= tableRect.top + 2 &&
        btnRect.bottom >= tableRect.bottom - 2 &&
        btnRect.width <= 28
      );
    };

    const ensureTableScrollHost = (wrapper: HTMLElement) => {
      const table = wrapper.querySelector('table') as HTMLTableElement | null;
      if (!table) return null;

      const parent = table.parentElement as HTMLElement | null;
      if (parent && parent.classList.contains('table-scroll')) {
        return { table, host: parent };
      }

      const host = document.createElement('div');
      host.className = 'table-scroll';
      wrapper.insertBefore(host, table);
      host.appendChild(table);
      return { table, host };
    };

    /** 버튼 위치를 테이블 실제 크기에 맞춰 업데이트 */
    const syncBtnPositions = (wrapper: HTMLElement, retry = 0) => {
      const wrapped = ensureTableScrollHost(wrapper);
      if (!wrapped) return;
      const { table, host } = wrapped;

      normalizeColGroup(table);

      const tableRect = table.getBoundingClientRect();
      const tableW = tableRect.width || table.offsetWidth;
      const tableH = tableRect.height || table.offsetHeight;
      const hostW = host.clientWidth || host.offsetWidth;
      const horizontalScrollbarH = Math.max(0, host.offsetHeight - host.clientHeight);
      if (tableW <= 0 || tableH <= 0) {
        wrapper.setAttribute(READY_ATTR, 'false');
        // 삽입 직후 레이아웃 미완성 상태에서 잘못된 좌표가 들어가면
        // invisible 버튼이 셀 위를 덮어 클릭 오프셋을 유발할 수 있음
        if (retry < 4) {
          requestAnimationFrame(() => syncBtnPositions(wrapper, retry + 1));
        }
        return;
      }
      const tableTop = host.offsetTop + table.offsetTop;
      const tableLeft = host.offsetLeft + table.offsetLeft;
      const hostLeft = host.offsetLeft;
      const rowBtn = wrapper.querySelector<HTMLElement>('.table-add-row-btn');
      const colBtn = wrapper.querySelector<HTMLElement>('.table-add-col-btn');
      const rightZone = wrapper.querySelector<HTMLElement>('.table-hover-right-zone');
      const bottomZone = wrapper.querySelector<HTMLElement>('.table-hover-bottom-zone');
      if (rowBtn) {
        rowBtn.style.left = `${hostLeft}px`;
        rowBtn.style.width = `${hostW}px`;
        rowBtn.style.top = `${tableTop + tableH + horizontalScrollbarH + ROW_GAP + ROW_SCROLL_EXTRA_GAP}px`;
      }
      if (colBtn) {
        const desiredLeft = hostLeft + hostW + COL_GAP;
        // 버튼은 가시 블록 우측 바깥에 고정 (열 추가해도 이동하지 않음)
        colBtn.style.left = `${desiredLeft}px`;
        colBtn.style.top = `${tableTop + 1}px`;
        colBtn.style.height = `${tableH}px`;
      }
      if (rightZone) {
        rightZone.style.left = `${hostLeft + hostW + 1}px`;
        rightZone.style.top = `${tableTop}px`;
        rightZone.style.width = `${Math.max(22, COL_GAP + 18)}px`;
        rightZone.style.height = `${tableH}px`;
      }
      if (bottomZone) {
        bottomZone.style.left = `${hostLeft}px`;
        bottomZone.style.top = `${tableTop + tableH + horizontalScrollbarH + 1 + ROW_SCROLL_EXTRA_GAP}px`;
        bottomZone.style.width = `${hostW}px`;
        bottomZone.style.height = `${Math.max(22, ROW_GAP + 18)}px`;
      }

      wrapper.setAttribute(READY_ATTR, 'true');
    };

    const attachControls = () => {
      const view = getReadyEditorView();
      if (!view) return;
      const editorDom = view.dom;
      const wrappers = editorDom.querySelectorAll<HTMLElement>('.tableWrapper');

      wrappers.forEach((wrapper) => {
        // 이미 버튼이 있으면 위치만 갱신
        if (wrapper.getAttribute(ATTR)) {
          if (!wrapper.hasAttribute(ACTIVE_ATTR)) {
            wrapper.setAttribute(ACTIVE_ATTR, 'false');
          }
          syncBtnPositions(wrapper);
          return;
        }
        wrapper.setAttribute(ATTR, 'true');
        wrapper.setAttribute(READY_ATTR, 'false');
        wrapper.setAttribute(ACTIVE_ATTR, 'false');

        // ── 행 추가 버튼 (하단) ──
        const addRowBtn = document.createElement('button');
        addRowBtn.className = 'table-add-row-btn';
        addRowBtn.type = 'button';
        addRowBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="7" y1="2" x2="7" y2="12"/><line x1="2" y1="7" x2="12" y2="7"/></svg>`;
        addRowBtn.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const table = wrapper.querySelector('table');
          if (table) {
            const ready = wrapper.getAttribute(READY_ATTR) === 'true';
            if (!ready) {
              debugLog('row-btn-ready-false-proceed', {
                clientX: e.clientX,
                clientY: e.clientY,
                domShape: getDomTableShape(table as HTMLTableElement),
              });
            }
            const lastRow = table.querySelector('tr:last-child');
            const lastCell = lastRow?.querySelector('td:last-child, th:last-child');
            if (lastCell) {
              debugLog('row-btn-triggered', { domShape: getDomTableShape(table as HTMLTableElement) });
              const activeView = getReadyEditorView();
              if (!activeView) return;
              const cellPos = activeView.posAtDOM(lastCell, 0);
              const insidePos = Math.min(cellPos + 1, editor.state.doc.content.size);
              const ok = editor.chain().focus(insidePos).addRowAfter().run();
              if (!ok) {
                editor.chain().focus(cellPos).addRowAfter().run();
              }
            }
          }
        });

        // ── 열 추가 버튼 (우측) ──
        const addColBtn = document.createElement('button');
        addColBtn.className = 'table-add-col-btn';
        addColBtn.type = 'button';
        addColBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="7" y1="2" x2="7" y2="12"/><line x1="2" y1="7" x2="12" y2="7"/></svg>`;
        addColBtn.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const table = wrapper.querySelector('table');
          if (table) {
            const ready = wrapper.getAttribute(READY_ATTR) === 'true';
            if (!ready) {
              debugLog('col-btn-ready-false-proceed', {
                clientX: e.clientX,
                clientY: e.clientY,
                domShape: getDomTableShape(table as HTMLTableElement),
              });
            }
            const firstRow = table.querySelector('tr:first-child');
            const lastCell = firstRow?.querySelector('td:last-child, th:last-child');
            if (lastCell) {
              debugLog('col-btn-triggered', {
                clientX: e.clientX,
                clientY: e.clientY,
                domShape: getDomTableShape(table as HTMLTableElement),
              });
              const activeView = getReadyEditorView();
              if (!activeView) return;
              const cellPos = activeView.posAtDOM(lastCell, 0);
              const insidePos = Math.min(cellPos + 1, editor.state.doc.content.size);
              const ok = editor.chain().focus(insidePos).addColumnAfter().run();
              if (ok) {
                requestAnimationFrame(() => syncBtnPositions(wrapper));
              } else {
                const fallback = editor.chain().focus(cellPos).addColumnAfter().run();
                if (fallback) {
                  requestAnimationFrame(() => syncBtnPositions(wrapper));
                }
              }
            }
          }
        });

        // 테이블 바깥 hover 영역(오른쪽/아래) — 노션처럼 버튼 노출 트리거
        const hoverRightZone = document.createElement('div');
        hoverRightZone.className = 'table-hover-right-zone';
        hoverRightZone.style.position = 'absolute';
        hoverRightZone.style.pointerEvents = 'auto';
        hoverRightZone.style.background = 'transparent';
        hoverRightZone.style.zIndex = '4';

        const hoverBottomZone = document.createElement('div');
        hoverBottomZone.className = 'table-hover-bottom-zone';
        hoverBottomZone.style.position = 'absolute';
        hoverBottomZone.style.pointerEvents = 'auto';
        hoverBottomZone.style.background = 'transparent';
        hoverBottomZone.style.zIndex = '4';

        // 버튼으로 이동할 때 사라지지 않도록 hover 대신 active 상태를 JS로 유지
        let hideTimer: number | null = null;
        const showControls = () => {
          if (hideTimer !== null) {
            window.clearTimeout(hideTimer);
            hideTimer = null;
          }
          wrapper.setAttribute(ACTIVE_ATTR, 'true');
        };
        const hideControlsDelayed = () => {
          if (hideTimer !== null) window.clearTimeout(hideTimer);
          hideTimer = window.setTimeout(() => {
            wrapper.setAttribute(ACTIVE_ATTR, 'false');
          }, 140);
        };

        wrapper.addEventListener('mouseenter', showControls);
        wrapper.addEventListener('mouseleave', hideControlsDelayed);
        addRowBtn.addEventListener('mouseenter', showControls);
        addRowBtn.addEventListener('mouseleave', hideControlsDelayed);
        addColBtn.addEventListener('mouseenter', showControls);
        addColBtn.addEventListener('mouseleave', hideControlsDelayed);
        hoverRightZone.addEventListener('mouseenter', showControls);
        hoverRightZone.addEventListener('mouseleave', hideControlsDelayed);
        hoverBottomZone.addEventListener('mouseenter', showControls);
        hoverBottomZone.addEventListener('mouseleave', hideControlsDelayed);
        wrapper.addEventListener('mousedown', showControls);

        disposers.push(() => {
          if (hideTimer !== null) window.clearTimeout(hideTimer);
          wrapper.removeEventListener('mouseenter', showControls);
          wrapper.removeEventListener('mouseleave', hideControlsDelayed);
          addRowBtn.removeEventListener('mouseenter', showControls);
          addRowBtn.removeEventListener('mouseleave', hideControlsDelayed);
          addColBtn.removeEventListener('mouseenter', showControls);
          addColBtn.removeEventListener('mouseleave', hideControlsDelayed);
          hoverRightZone.removeEventListener('mouseenter', showControls);
          hoverRightZone.removeEventListener('mouseleave', hideControlsDelayed);
          hoverBottomZone.removeEventListener('mouseenter', showControls);
          hoverBottomZone.removeEventListener('mouseleave', hideControlsDelayed);
          wrapper.removeEventListener('mousedown', showControls);
        });

        wrapper.appendChild(hoverRightZone);
        wrapper.appendChild(hoverBottomZone);
        wrapper.appendChild(addRowBtn);
        wrapper.appendChild(addColBtn);
        requestAnimationFrame(() => syncBtnPositions(wrapper));
      });
    };

    let rafId: number | null = null;
    let cleanup: (() => void) | null = null;

    const attach = () => {
      const view = getReadyEditorView();
      if (!view) {
        rafId = requestAnimationFrame(attach);
        return;
      }

      const editorDom = view.dom;

      // 초기 실행 + 에디터 변경 시 재실행 (RAF로 DOM 안정화 후)
      let controlsRafId = requestAnimationFrame(attachControls);
      const onUpdate = () => {
        cancelAnimationFrame(controlsRafId);
        controlsRafId = requestAnimationFrame(attachControls);
      };
      editor.on('update', onUpdate);

      // 클릭 직전(capture phase) colgroup을 정규화해 posAtCoords 오프셋 밀림을 방지
      const onMouseDownCapture = (e: MouseEvent) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        const wrapper = target.closest('.tableWrapper') as HTMLElement | null;
        if (!wrapper) return;
        const table = wrapper.querySelector('table') as HTMLTableElement | null;
        if (!table) return;
        lastMouseDownInTableAt = Date.now();
        lastMouseDownTag = target.tagName;
        const inColBtn = !!target.closest('.table-add-col-btn');
        const inRowBtn = !!target.closest('.table-add-row-btn');
        const inCell = !!target.closest('td,th');
        lastMouseDownKind = inColBtn ? 'col-btn' : inRowBtn ? 'row-btn' : inCell ? 'cell' : 'other';
        debugLog('mousedown-in-table', {
          targetTag: target.tagName,
          targetClass: target.className || '',
          targetKind: lastMouseDownKind,
          domShapeBeforeNormalize: getDomTableShape(table),
          docShapeAtSelection: getDocTableShapeAtSelection(),
        });

        if (!inCell) {
          return;
        }

        normalizeColGroup(table);
        requestAnimationFrame(() => {
          debugLog('mousedown-post-raf-shape', {
            targetTag: target.tagName,
            targetKind: lastMouseDownKind,
            domShapeAfterNormalize: getDomTableShape(table),
            docShapeAtSelection: getDocTableShapeAtSelection(),
          });
        });
      };
      editorDom.addEventListener('mousedown', onMouseDownCapture, true);

      const onTransaction = ({ transaction }: { transaction: { docChanged: boolean; steps: unknown[] } }) => {
        if (!transaction.docChanged) return;
        const elapsed = Date.now() - lastMouseDownInTableAt;
        if (elapsed > 1000) return;
        const activeView = getReadyEditorView();
        if (!activeView) return;
        const selectedCell = activeView.dom.querySelector('td.ProseMirror-selectednode, th.ProseMirror-selectednode') as HTMLElement | null;
        const table = selectedCell?.closest('table') as HTMLTableElement | null;
        debugLog('doc-changed-near-table-mousedown', {
          elapsedMs: elapsed,
          lastMouseDownTag,
          lastMouseDownKind,
          stepsLength: transaction.steps.length,
          domShape: table ? getDomTableShape(table) : null,
          docShapeAtSelection: getDocTableShapeAtSelection(),
        });
      };
      editor.on('transaction', onTransaction);

      // 열 리사이즈 후에도 위치 갱신
      const observer = new MutationObserver(() => {
        requestAnimationFrame(() => {
          const activeView = getReadyEditorView();
          if (!activeView) return;
          const wrappers = activeView.dom.querySelectorAll<HTMLElement>('.tableWrapper');
          wrappers.forEach(syncBtnPositions);
        });
      });
      observer.observe(editorDom, { subtree: true, attributes: true, attributeFilter: ['style', 'colwidth'] });

      cleanup = () => {
        cancelAnimationFrame(controlsRafId);
        editor.off('update', onUpdate);
        editor.off('transaction', onTransaction);
        editorDom.removeEventListener('mousedown', onMouseDownCapture, true);
        disposers.forEach((dispose) => dispose());
        observer.disconnect();
        if (TABLE_DEBUG && w.getTableDebugSummary) {
          delete w.getTableDebugSummary;
        }
      };
    };

    attach();
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      cleanup?.();
    };
  }, [editor, TABLE_DEBUG]);

  return { tableDragRef };
}
